import { format, formatDistanceToNow, parseISO } from 'date-fns'

/**
 * Display helpers used across alert/case/transaction views.
 * Always operate on validated data — no defensive `?? ''` fallbacks here;
 * upstream type guards or zod parses should already have rejected bad input.
 */

/** Format Naira amount with thousands separators. Backend stores kobo as integer; pass minor units. */
export function formatNaira(minor: number): string {
  const major = minor / 100
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 2,
  }).format(major)
}

/**
 * Mask a NUBAN for display: 0123456789 → 012****789.
 * Keeps leading 3 + trailing 3, masks middle 4. Unmasked NUBANs are PII.
 */
export function maskNuban(nuban: string): string {
  if (nuban.length !== 10) return nuban
  return `${nuban.slice(0, 3)}****${nuban.slice(7)}`
}

/**
 * Date helpers tolerate null/undefined/empty/non-ISO input by
 * returning the supplied placeholder ("—" by default). The
 * underlying date-fns `parseISO` throws "Cannot read properties of
 * undefined (reading 'split')" on undefined and "Invalid time
 * value" on a Date that fails toISOString — both crash a page
 * render. Failing soft is the right default for display helpers;
 * if a caller wants strict, the date-fns API is one import away.
 */
function safeParse(iso: string | null | undefined): Date | null {
  if (iso === null || iso === undefined || iso === '') return null
  const d = parseISO(iso)
  if (Number.isNaN(d.getTime())) return null
  return d
}

export function formatDateTime(iso: string | null | undefined, fallback = '—'): string {
  const d = safeParse(iso)
  return d ? format(d, 'd MMM yyyy, HH:mm') : fallback
}

export function formatDate(iso: string | null | undefined, fallback = '—'): string {
  const d = safeParse(iso)
  return d ? format(d, 'd MMM yyyy') : fallback
}

export function formatTimeAgo(iso: string | null | undefined, fallback = '—'): string {
  const d = safeParse(iso)
  return d ? formatDistanceToNow(d, { addSuffix: true }) : fallback
}

/** Pretty-print a transaction channel for display. */
export function formatChannel(code: string): string {
  const map: Record<string, string> = {
    pos: 'POS',
    nip: 'NIP',
    rtgs: 'RTGS',
    ussd: 'USSD',
    mobile_app: 'Mobile App',
    internet_banking: 'Internet Banking',
    agent: 'Agent',
    atm: 'ATM',
  }
  return map[code] ?? code.toUpperCase()
}
